/**
 * Clinic time (RL-1896 / RL-1897). Every branch runs on Asia/Kuala_Lumpur
 * (UTC+8, no daylight saving); the API sends and takes ISO 8601 instants. Use
 * these helpers for date and date-time fields instead of slicing an ISO
 * string (that is UTC: 09:30 in the clinic showed as 01:30) or `new Date(x)`
 * on a field value (that is the computer's own time zone).
 */
export const CLINIC_TZ = 'Asia/Kuala_Lumpur';

function parts(value: Date | string) {
  const all = new Intl.DateTimeFormat('en-CA', {
    timeZone: CLINIC_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(typeof value === 'string' ? new Date(value) : value);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    all.find((p) => p.type === type)?.value ?? '';
  return {
    date: `${part('year')}-${part('month')}-${part('day')}`,
    time: `${part('hour')}:${part('minute')}`,
  };
}

/** An instant as a `datetime-local` value in clinic time ("" for none). */
export function toClinicInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const p = parts(iso);
  return `${p.date}T${p.time}`;
}

/** A `datetime-local` value, read as clinic time, as ISO with +08:00. */
export function fromClinicInput(value: string): string {
  const [date, time = '00:00'] = value.split('T');
  return `${date}T${time.length === 5 ? `${time}:00` : time}+08:00`;
}

/** The clinic's calendar date (YYYY-MM-DD) of an instant; default now. */
export function clinicDate(value: Date | string = new Date()): string {
  return parts(value).date;
}

/**
 * A stored day as YYYY-MM-DD: a plain date stays as it is; a date-time is
 * read as the clinic's day (not its UTC day).
 */
export function clinicDay(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : clinicDate(value);
}
